import React, { useState, useContext, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { LoadingContext, M_StateActions } from "src/M_Components";
import RoutePreviewTable from "../../components/routePreview/routePreviewTable";
import { useTranslation } from "react-i18next";
import { BufferService } from "src/M_Components/M_Utils/buffer.service";
import { isFailurePayload } from "src/components/utils/apiResponse";
// Dynamic select box (server-driven, no preloaded options)
import DynamicSelectBox from "src/components/Forms/DynamicSelectBox";

export default function RoutePreview({ onBack, toastMessages }) {
  const { t } = useTranslation();
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);

  // Per-tab namespace + isolated selection (store only the ID)
  const selectedTab = useSelector((s) =>
    s.StateKeyValue.find((o) => o.key === "selectedTab")?.value
  );

  // --------------------------------------------------------------------------------------
  //  RoutePreview uses the same per-tab isolation pattern as Carrier pages.
  // - Namespace = current Redux 'selectedTab'
  // - We keep 'id' in sync with Redux so tab switching restores the correct state.
  // --------------------------------------------------------------------------------------
  const [id, setId] = useState(() => selectedTab); // current tab id
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const sessionKeySelected = "routePreview_selectedRouteId"; // unique key for this sub-tab

  /**
   * Indicates that a restore attempt has completed for the currently active tab.
   *
   * Purpose:
   * - Ensures the default selection strategy runs only after per-tab restoration is attempted.
   * - Prevents default selection from overriding a saved selection during tab switches.
   */
  const [hasRestoredFromSession, setHasRestoredFromSession] = useState(false);

  //============================================================================================================
  // Upsert a single key/value under the tab namespace (merge, do not replace)
  //============================================================================================================
  const upsertSession = (tabId, kv) => {
    // kv: { key: string, value: any }
    const current = M_State.find((o) => o.key === tabId)?.value || [];
    const filtered = current.filter((e) => e.key !== kv.key);
    dispatch(M_StateActions.addM_State({ key: tabId, value: [...filtered, kv] }));
  };

  //============================================================================================================
  // EFFECT
  // Restore per-tab selection for this sub-tab
  //============================================================================================================
  useEffect(() => {
    // ----------------------------------------------------------------------------------
    //  Rehydrate from the current tab namespace. This mirrors Carrier* pages.
    // ----------------------------------------------------------------------------------
    const currentTabId = selectedTab || id;
    if (!currentTabId) return;

    // Mark that restoration is pending for the active tab id.
    setHasRestoredFromSession(false);

    // Keep local id aligned with the active tab id.
    if (currentTabId !== id) setId(currentTabId);

    // Restore the saved route selection for the active tab, when present.
    const session = M_State.find((obj) => obj.key === currentTabId)?.value;
    const savedId = session?.find((x) => x.key === sessionKeySelected)?.value;

    // When no saved selection exists for this tab, clear local selection so default logic can decide.
    if (savedId !== undefined) setSelectedRouteId(savedId);
    else setSelectedRouteId(null);

    // Mark restoration attempt as complete for the active tab.
    setHasRestoredFromSession(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_State, selectedTab, id]);

  //============================================================================================================
  // EFFECT
  // Persist per-tab selection for this sub-tab
  //============================================================================================================
  useEffect(() => {
    const currentTabId = id || selectedTab;
    if (!currentTabId) return;

    upsertSession(currentTabId, { key: sessionKeySelected, value: selectedRouteId });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, selectedTab, selectedRouteId]);

  //============================================================================================================
  // EFFECT
  // Default selection strategy for Route (no preloaded options)
  // Dont fetch large pages. If no selection is persisted, fetch only the first active Route (PageSize=1)
  // ordered by Description asc (fallback to Name if your API resolves labels differently).
  //  Bootstrap only once per tab.
  // - Prevent re-applying a default when the user clears selection.
  // - This bootstrap is guarded per tab id, so each tab can resolve a default independently.
  //============================================================================================================
  const bootstrappedTabRef = useRef(null);
  useEffect(() => {
    const currentTabId = id || selectedTab;
    if (!currentTabId) return;

    // Wait until session restore has been attempted for this tab.
    if (!hasRestoredFromSession) return;

    // When a selection exists (restored or user-selected), no default is required.
    if (selectedRouteId != null) {
      bootstrappedTabRef.current = currentTabId;
      return;
    }

    // Prevent repeated bootstrap attempts for the same tab id.
    if (bootstrappedTabRef.current === currentTabId) return;

    const routesBuffer = new BufferService("Routes", ctx.DEFAULTURL);
    (async () => {
      try {
        const order = encodeURIComponent("Description asc"); // keep deterministic first item
        const filter = encodeURIComponent("Inactive eq false");
        const res = await routesBuffer.filter(
          `?Page=1&PageSize=1&OrderBy=${order}&Filter=${filter}`
        );
        if (isFailurePayload(res)) return;

        const items = Array.isArray(res?.Items)
          ? res.Items
          : Array.isArray(res)
            ? res
            : [];
        const first = items?.[0];
        if (first?.Id != null) setSelectedRouteId(first.Id);
      } catch {
        // silent fallback: leave unselected
      } finally {
        // Ensure default selection is not re-applied for the same tab id later.
        bootstrappedTabRef.current = currentTabId;
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.DEFAULTURL, hasRestoredFromSession, selectedRouteId, id, selectedTab]);

  return (
    <div className="divGlobalSettings">
      <div className="subTitleSettings">
        <div>
          <div style={{ color: ctx.secondaryColor }}>{t("RoutePreview")}</div>
          <div className="labelSubtitleSettings">{t("InfoRoutePreview")}</div>
        </div>
      </div>

      <div
        className="firstDivContainer"
        style={{
          marginBottom: "30px",
          padding: "20px",
          border: "2px solid var(--color-red-dark)",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <span
          style={{
            fontWeight: "bold",
            color: "var(--color-red-dark)",
            fontSize: "20px",
            display: "inline-block",
            marginBottom: "20px",
            borderBottom: "2px solid var(--color-red-dark)",
          }}
        >
          {t("Route")}
        </span>

        {/* Select box to select the Route */}
        <div style={{ marginBottom: "20px" }}>
          <DynamicSelectBox
            // Use DynamicSelectBox to avoid loading large option lists upfront
            label={t("Route")}
            tableName="Routes"
            // Display field: prefer Description, then Name, then Code/Id (handled inside DynamicSelectBox mapRow)
            groupBy="Description"
            valueId={selectedRouteId}
            required
            pageSize={20}
            // When the user selects a route, store only the id
            onChange={(opt) => {
              // --------------------------------------------------------------------------------------
              //  Respect user clearing. Do not auto-restore a default afterward for the same tab.
              // Setting selectedRouteId to null will not trigger the default bootstrap again for that tab,
              // because bootstrappedTabRef guards bootstrap attempts per tab id.
              // --------------------------------------------------------------------------------------
              setSelectedRouteId(opt?.value ?? null);
            }}
          />
        </div>

        {/* Central content with the table */}
        {selectedRouteId && (
          <div style={{ marginBottom: "20px" }}>
            <span
              style={{
                fontWeight: "bold",
                color: "var(--color-red-dark)",
                fontSize: "14px",
                display: "inline-block",
                marginBottom: "5px",
              }}
            >
              {t("RoutePreview")}
            </span>
            <RoutePreviewTable
              routeId={selectedRouteId} /* Table displaying route details based on a given Route ID */
            />
          </div>
        )}
      </div>
    </div>
  );
}

RoutePreview.propTypes = {
  onBack: PropTypes.func.isRequired,
  toastMessages: PropTypes.func.isRequired,
};
